import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { useAuthStore } from './auth'
import type { User, UserUpdatePayload, AvatarUploadPayload } from '@/types/user'

interface UniqueCheckResponse {
  valid: boolean
}

interface RoleCheckResponse {
  role_changed: boolean
  current_role: string
  token_role: string
}

export const useUserStore = defineStore('user', () => {
  const currentUser = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const avatarLoading = ref(false)

  const fetchCurrentUser = async () => {
    isLoading.value = true
    error.value = null
    
    try {
      const response = await api.get(API_ROUTES.USER_GET_CURRENT)
      currentUser.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch user data'
      console.error('Error fetching user:', err)
      
      // The API interceptor already handles token_not_valid (retry with cookies)
      // and 401 (refresh token flow). No need to duplicate that logic here.
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const clearCurrentUser = () => {
    currentUser.value = null
  }

  const updateProfile = async (payload: UserUpdatePayload) => {
    isLoading.value = true
    error.value = null

    try {
      const cleanPayload = {
        ...payload,
        gender: payload.gender?.toString(),
        current_job_role: payload.current_job_role?.toString(),
        country_code_iso_2: payload.country_code_iso_2?.toString()
      };

      const response = await api.put(API_ROUTES.USER_UPDATE, cleanPayload);
      currentUser.value = response.data;
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update profile'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateAvatar = async (payload: AvatarUploadPayload) => {
    avatarLoading.value = true
    error.value = null

    try {
      const response = await api.put(
        API_ROUTES.USER_UPLOAD_AVATAR, 
        payload 
      );
      if (currentUser.value) {
        currentUser.value.avatar_image = response.data.avatar_image;
      }
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update avatar';
      throw err;
    } finally {
      avatarLoading.value = false;
    }
  }

  const changeAvatar = async () => {
    avatarLoading.value = true
    error.value = null

    try {
      const response = await api.delete(API_ROUTES.DELETE_USER_AVATAR)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to change avatar'
      throw err
    } finally {
      avatarLoading.value = false
    }
  } 

  const deleteAvatar = async () => {
    avatarLoading.value = true
    error.value = null

    try {
      const response = await api.delete(API_ROUTES.DELETE_USER_AVATAR)
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to delete avatar'
      throw err
    } finally {
      avatarLoading.value = false
    }
  } 

  const checkUsernameUniqueness = async (username: string): Promise<UniqueCheckResponse> => {
    const payload = {
      username: username
    }
    const response = await api.post(API_ROUTES.CHECK_USER_EXISTS, payload)
    return response.data
  }

  const checkEmailUniqueness = async (email: string): Promise<UniqueCheckResponse> => {
    const payload = {
      email: email
    }
    const response = await api.post(API_ROUTES.CHECK_EMAIL_EXISTS, payload )
    return response.data
  }

  const checkUserRole = async (): Promise<RoleCheckResponse> => {
    try {
      // Flow: JWT token -> user_role cookie -> empty string
      
      // Priority 1: Try JWT token from localStorage
      const { decodeJWT } = await import('@/utils/jwtDecoder')
      const token = localStorage.getItem('token')
      
      if (token) {
        try {
          const decoded = decodeJWT(token)
          if (decoded) {
            // Extract role from token (check common field names)
            const tokenRole = (decoded.role || decoded.user_role || decoded.userRole || '').toString().toUpperCase()
            
            if (tokenRole) {
              return {
                role_changed: false,
                current_role: tokenRole,
                token_role: tokenRole
              }
            }
          }
        } catch (err) {
          console.warn('[UserStore] Failed to decode token:', err)
        }
      }
      
      // Priority 2: Try user_role cookie (if token not found or invalid)
      const { getRoleFromCookie } = await import('@/utils/roleHelpers')
      const cookieRole = getRoleFromCookie()
      if (cookieRole) {
        return {
          role_changed: false,
          current_role: cookieRole,
          token_role: cookieRole
        }
      }
      
      // No role found - return empty role
      return {
        role_changed: false,
        current_role: '',
        token_role: ''
      }
    } catch (err: any) {
      error.value = err.message || 'Failed to check user role from token'
      console.error('Error checking user role from token:', err)
      // Return empty role instead of throwing, to prevent breaking existing code
      return {
        role_changed: false,
        current_role: '',
        token_role: ''
      }
    }
  }

  const logout = async () => {
    const authStore = useAuthStore()
    // Await to ensure all cleanup completes (clearing localStorage, cookies, etc.)
    await authStore.logout()
    currentUser.value = null
    // Don't redirect here - let callers handle redirect after logout completes
    // This ensures cleanup finishes before navigation
  }

  return {
    currentUser,
    isLoading,
    error,
    avatarLoading,
    fetchCurrentUser,
    clearCurrentUser,
    updateProfile,
    updateAvatar,
    logout,
    changeAvatar,
    deleteAvatar,
    checkUsernameUniqueness,
    checkEmailUniqueness,
    checkUserRole
  }
}) 